import type { Conditions } from '@risksmart-app/form-configuration/src/field-types/types';
import type { FormFieldConfig } from '@risksmart-app/shared/forms/types';

/**
 * Condition `propertyKey` canonicalisation shared by the form editor's
 * save path (`useFormEditorSave`) and live-preview path
 * (`convertEditorStateToFormData`).
 *
 * The editor's IF-field picker stores `reg.fieldId` — a field's React key in
 * the host form JSX (conventionally lowercase, e.g. `title`) for standard
 * fields, or JSON Schema scope (`#/properties/{path}`) for custom attributes.
 * Conditions are evaluated against form *values*, which key standard fields by
 * canonical `FieldId` (`Title`) and custom attributes by server format
 * (`CustomAttributeData.{path}`). Both call sites must translate the editor key
 * space into the value key space; only the per-key strategy differs (save-time
 * resolves freshly-created temp ids to real ids first; the preview keeps temp
 * ids), so callers pass their own `remapKey` to {@link remapConditionPropertyKeys}.
 */

/**
 * Section/ordering tables store JSON Schema scope format (`#/properties/{path}`)
 * while form_field_configuration + runtime form values store server format
 * (`CustomAttributeData.{path}`). Convert scope → server format; pass anything
 * else through unchanged.
 */
export const scopeToServerId = (id: string): string =>
  id.startsWith('#/properties/')
    ? `CustomAttributeData.${id.slice('#/properties/'.length)}`
    : id;

/**
 * Builds a lookup from a standard field's lowercase React key → its canonical
 * `FieldId`, from a form's registry config. Used to translate editor IF-field
 * keys into the casing the form value is stored under.
 */
export const buildStandardFieldCanonicalMap = (
  formConfig: Record<string, FormFieldConfig> | undefined
): Map<string, string> => {
  const map = new Map<string, string>();
  if (formConfig) {
    for (const f of Object.values(formConfig)) {
      map.set(f.fieldId.toLowerCase(), f.fieldId);
    }
  }

  return map;
};

/**
 * Standard-field fallback: canonical casing when the key is known to the form
 * registry, otherwise the key unchanged (e.g. a sibling new field's temp id).
 */
export const canonicaliseStandardFieldKey = (
  key: string,
  standardFieldCanonicalById: Map<string, string>
): string => standardFieldCanonicalById.get(key.toLowerCase()) ?? key;

/**
 * Applies a per-key remap across every token / tokenGroup of a `Conditions`
 * value, preserving structure. The remap strategy is supplied by the caller.
 */
export const remapConditionPropertyKeys = (
  conditions: Conditions | null,
  remapKey: (key: string) => string
): Conditions | null => {
  if (!conditions) {
    return null;
  }

  return {
    ...conditions,
    tokenGroups: conditions.tokenGroups.map((group) => {
      if ('operation' in group) {
        return {
          ...group,
          tokens: group.tokens.map((t) => ({
            ...t,
            propertyKey: remapKey(t.propertyKey),
          })),
        };
      }

      return { ...group, propertyKey: remapKey(group.propertyKey) };
    }),
  };
};
